import React from 'react';
import { IcoAlert } from './Icons';

/**
 * A69 — one crashing component no longer takes the whole app down with it.
 *
 * React unmounts the entire tree when a render throws and nothing catches it, so a single mistake
 * anywhere below the router produced a blank white page: no message, no nav, no way back. That is
 * exactly how the A68 Delivered-tab crash reached a client — an icon used but not imported threw a
 * ReferenceError while rendering one badge, and the operator saw an empty screen with nothing to
 * report but "it went blank".
 *
 * This keeps the failure the size of the thing that failed. The sidebar and nav stay alive (the
 * boundary sits inside Layout), the user is told what happened, and they can retry or navigate away
 * instead of force-reloading and hoping.
 *
 * Keyed on pathname by the caller so moving to another page clears a caught error automatically —
 * without that the boundary latches and every later route renders the error card too.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Blank screens are unreportable. Leave a trace the browser console keeps, so the next person
    // asked "what did it say?" has an answer.
    console.error('[ErrorBoundary]', error, info && info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = String((this.state.error && this.state.error.message) || this.state.error);
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: 560, width: '100%', background: '#fff', border: '1px solid #fecaca', borderRadius: 12, padding: 24, boxShadow: 'var(--mc-e2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626', fontWeight: 700, fontSize: 15 }}>
            <IcoAlert size={18} /> This page hit an error
          </div>
          <p style={{ color: '#475569', fontSize: 13, lineHeight: 1.6, margin: '12px 0 0' }}>
            Nothing you did caused this and no data was changed. The rest of the app still works —
            use the menu to carry on, or try this page again.
          </p>
          <pre style={{ margin: '14px 0 0', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg}</pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              onClick={() => this.setState({ error: null })}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
